/**
 * CareerDashboard — ★ Fase 33: pannello carriera personale gamificato (tenant ES).
 * Mostra livello, guadagni stimati del mese (USD), obiettivi con "quanto manca",
 * streak promozione e avvisi (cartellino giallo, rischio retrocessione).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  ChevronLeft, ChevronRight, Wallet, TrendingUp, AlertTriangle, Trophy,
  Users, CalendarDays, KanbanSquare, Table2, ArrowUpRight, Flame,
} from 'lucide-react';
import { fmtUSD, LEVEL_META, monthLabel, StreakDots } from './careerUtils';

function EarningsChip({ label, value, testId, highlight }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border px-3 py-2 min-w-[110px] ${
        highlight ? 'bg-emerald-50 border-emerald-200' : 'bg-card'
      }`}
    >
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`font-semibold tabular-nums ${highlight ? 'text-emerald-700' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function ObjectiveBar({ label, doneLabel, targetLabel, pct, barClass, testId }) {
  const val = Math.max(0, Math.min(100, pct || 0));
  const reached = (pct || 0) >= 100;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">
          {doneLabel} / {targetLabel}
          {reached && (
            <span className="inline-flex items-center gap-0.5 ml-1.5 text-emerald-600 text-[10px] font-semibold">
              <Trophy className="w-3 h-3" />
            </span>
          )}
        </span>
      </div>
      <Progress value={val} className={reached ? '[&>div]:bg-emerald-500' : (barClass || '')} />
    </div>
  );
}

export function CareerDashboard({ data, onChangeMonth, loading }) {
  const { t, i18n } = useTranslation();
  const [plan, setPlan] = useState(null);
  const [tableOpen, setTableOpen] = useState(false);

  useEffect(() => {
    api.get('/career/plan').then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const level = data?.career_level || 'setter';
  const meta = LEVEL_META[level] || LEVEL_META.setter;
  const LevelIcon = meta.icon;
  const earnings = data?.earnings || {};
  const obj = data?.objectives || {};
  const warnings = data?.warnings || [];

  const mLabel = useMemo(
    () => (data ? monthLabel(i18n.language, data.year, data.month) : ''),
    [data, i18n.language],
  );

  const levelName = t(`career.levels.${level}`, level);

  if (loading || !data) {
    return (
      <Card data-testid="career-dashboard-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden border-emerald-100" data-testid="career-dashboard">
      {/* Header livello + mese */}
      <div className="bg-gradient-to-r from-emerald-900 to-teal-800 text-white px-5 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15">
              <LevelIcon className="w-5 h-5" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-wide opacity-80">
                {t('career.title', 'Mi Carrera')}
              </div>
              <div className="font-serif text-xl flex items-center gap-2" data-testid="career-level-name">
                {levelName}
                {data.career_milestone && (
                  <Badge className="bg-amber-400/90 text-amber-950 border-0 text-[10px]" data-testid="career-milestone-badge">
                    <Trophy className="w-3 h-3 mr-1" />{t('career.milestone', 'Meta alcanzada')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="icon" variant="ghost"
              className="h-8 w-8 text-white hover:bg-white/15 hover:text-white"
              onClick={() => onChangeMonth(-1)}
              data-testid="career-month-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[130px] text-center" data-testid="career-month-label">
              {mLabel}
            </span>
            <Button
              size="icon" variant="ghost"
              className="h-8 w-8 text-white hover:bg-white/15 hover:text-white"
              onClick={() => onChangeMonth(1)}
              data-testid="career-month-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <CardContent className="p-5 space-y-5">
        {/* Avvisi */}
        {warnings.includes('yellow_card') && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="career-warning-yellow-card">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            {t('career.yellowCard', 'Tarjeta amarilla: este mes estás por debajo del {{pct}}% de shows.', { pct: obj.min_show_pct || 5 })}
          </div>
        )}
        {warnings.includes('termination_risk') && (
          <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900" data-testid="career-warning-termination">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            {t('career.terminationRisk', 'Riesgo de rescisión: segundo mes consecutivo por debajo del {{pct}}% de shows.', { pct: obj.min_show_pct || 5 })}
          </div>
        )}
        {warnings.includes('milestone_at_risk') && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="career-warning-milestone">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            {t('career.milestoneAtRisk', 'Atención: este mes no alcanzas tu objetivo — tu fijo podría volver al nivel base.')}
          </div>
        )}

        {/* Guadagno stimato */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" />
              {t('career.monthEstimate', 'Ganancia estimada del mes')}
            </div>
            <div className="font-serif text-4xl text-emerald-800 tabular-nums" data-testid="career-total-earnings">
              {fmtUSD(earnings.total)}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <EarningsChip label={t('career.fixed', 'Fijo mensual')} value={fmtUSD(earnings.fixed)} testId="career-earnings-fixed" />
            {level === 'setter' && (
              <EarningsChip label={t('career.bonus', 'Bono')} value={fmtUSD(earnings.bonus)} testId="career-earnings-bonus" highlight={earnings.bonus > 0} />
            )}
            {level === 'junior' && (
              <EarningsChip
                label={t('career.commissionInfo', 'Comisión ({{pct}}%)', { pct: earnings.commission_pct })}
                value={fmtUSD(earnings.commission)}
                testId="career-earnings-commission"
                highlight={earnings.commission > 0}
              />
            )}
            {level === 'senior' && (
              <>
                <EarningsChip
                  label={t('career.personalCommission', 'Comisión personal ({{pct}}%)', { pct: earnings.personal_commission_pct })}
                  value={fmtUSD(earnings.personal_commission)}
                  testId="career-earnings-personal-commission"
                />
                <EarningsChip
                  label={t('career.teamCommission', 'Comisión del equipo ({{pct}}%)', { pct: earnings.team_commission_pct })}
                  value={fmtUSD(earnings.team_commission)}
                  testId="career-earnings-team-commission"
                  highlight={earnings.team_commission > 0}
                />
              </>
            )}
          </div>
        </div>

        {/* Obiettivi per livello */}
        <div className="space-y-4">
          <div className="text-sm font-semibold flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-emerald-700" />
            {t('career.objectives', 'Mis objetivos')}
          </div>

          {level === 'setter' && (
            <>
              <ObjectiveBar
                label={t('career.showPct', '% de Shows (shows / leads asignados)')}
                doneLabel={`${data.show_pct}%`}
                targetLabel={obj.next_bonus_step ? `${obj.next_bonus_step.pct}%` : '100%'}
                pct={obj.next_bonus_step ? (data.show_pct / obj.next_bonus_step.pct) * 100 : 100}
                barClass={LEVEL_META.setter.bar}
                testId="career-obj-show-pct"
              />
              <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
                <div className="text-muted-foreground" data-testid="career-next-bonus-info">
                  {data.leads_assigned === 0 ? (
                    t('career.noLeads', 'Aún no tienes leads asignados este mes.')
                  ) : obj.next_bonus_step ? (
                    t('career.needShows', 'Te faltan {{count}} shows para el bono de {{bonus}} ({{pct}}%)', {
                      count: obj.shows_needed_next_step,
                      bonus: fmtUSD(obj.next_bonus_step.bonus),
                      pct: obj.next_bonus_step.pct,
                    })
                  ) : (
                    t('career.maxBonus', '¡Has alcanzado el bono máximo!')
                  )}
                  <span className="ml-2 text-xs">
                    ({data.shows} {t('career.shows', 'shows')} · {data.leads_assigned} {t('career.leadsAssigned', 'leads')})
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setTableOpen(true)} data-testid="career-bonus-table-btn">
                  <Table2 className="w-3.5 h-3.5 mr-1.5" />
                  {t('career.bonusTableBtn', 'Ver tabla de bonos')}
                </Button>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 rounded-lg border bg-muted/40 px-3 py-2">
                <span className="text-sm flex items-center gap-1.5">
                  <Flame className="w-4 h-4 text-amber-600" />
                  {t('career.promotionStreakSetter', 'Ascenso a Junior: meses seguidos con ≥{{pct}}% de shows', { pct: obj.promotion_pct })}
                </span>
                <span className="flex items-center gap-2 text-sm font-medium">
                  <StreakDots value={data.promotion_streak || 0} total={obj.promotion_streak_target || 3} testId="career-promotion-streak" />
                  {t('career.streakOf', '{{n}} de {{total}} meses', { n: data.promotion_streak || 0, total: obj.promotion_streak_target || 3 })}
                </span>
              </div>
            </>
          )}

          {level === 'junior' && (
            <>
              <ObjectiveBar
                label={t('career.monthlyTarget', 'Objetivo mensual de venta')}
                doneLabel={fmtUSD(data.sold)}
                targetLabel={fmtUSD(obj.monthly_sales_target)}
                pct={(data.sold / (obj.monthly_sales_target || 1)) * 100}
                barClass={LEVEL_META.junior.bar}
                testId="career-obj-monthly-sales"
              />
              <div className="text-xs text-muted-foreground" data-testid="career-monthly-remaining">
                {obj.monthly_sales_remaining > 0
                  ? t('career.remaining', 'Faltan {{amount}}', { amount: fmtUSD(obj.monthly_sales_remaining) })
                  : t('career.targetReached', '¡Objetivo del mes alcanzado!')}
              </div>
              <ObjectiveBar
                label={t('career.quarterTarget', 'Mínimo trimestral')}
                doneLabel={fmtUSD(obj.quarter_sold)}
                targetLabel={fmtUSD(obj.quarterly_sales_min)}
                pct={(obj.quarter_sold / (obj.quarterly_sales_min || 1)) * 100}
                barClass="[&>div]:bg-teal-600"
                testId="career-obj-quarter-sales"
              />
              <div className="flex items-center justify-between flex-wrap gap-2 rounded-lg border bg-muted/40 px-3 py-2">
                <span className="text-sm flex items-center gap-1.5">
                  <Flame className="w-4 h-4 text-amber-600" />
                  {t('career.promotionStreakJunior', 'Ascenso a Senior: meses seguidos con ≥{{amount}} vendidos', { amount: fmtUSD(obj.monthly_sales_target) })}
                </span>
                <span className="flex items-center gap-2 text-sm font-medium">
                  <StreakDots value={data.promotion_streak || 0} total={obj.promotion_streak_target || 6} testId="career-promotion-streak" />
                  {t('career.streakOf', '{{n}} de {{total}} meses', { n: data.promotion_streak || 0, total: obj.promotion_streak_target || 6 })}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {t('career.milestoneBonus', 'Al alcanzar la meta: bono de {{amount}} + fijo {{fixed}} + comisión {{pct}}%', {
                  amount: fmtUSD(obj.milestone_bonus),
                  fixed: fmtUSD(1200),
                  pct: 5,
                })}
              </div>
            </>
          )}

          {level === 'senior' && (
            <>
              <ObjectiveBar
                label={t('career.teamTarget', 'Objetivo del equipo (ventas del mes)')}
                doneLabel={fmtUSD(data.team?.team_total_sold)}
                targetLabel={fmtUSD(obj.team_monthly_sales_target)}
                pct={((data.team?.team_total_sold || 0) / (obj.team_monthly_sales_target || 1)) * 100}
                barClass={LEVEL_META.senior.bar}
                testId="career-obj-team-sales"
              />
              <div className="text-xs text-muted-foreground" data-testid="career-team-remaining">
                {obj.team_remaining > 0
                  ? t('career.remaining', 'Faltan {{amount}}', { amount: fmtUSD(obj.team_remaining) })
                  : t('career.targetReached', '¡Objetivo del mes alcanzado!')}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 rounded-lg border bg-muted/40 px-3 py-2">
                <span className="text-sm flex items-center gap-1.5">
                  <Flame className="w-4 h-4 text-amber-600" />
                  {t('career.retroStreak', 'Override 3% retro: meses seguidos con equipo ≥ {{amount}}', { amount: fmtUSD(obj.team_monthly_sales_target) })}
                </span>
                <span className="flex items-center gap-2 text-sm font-medium">
                  <StreakDots value={data.retro_streak || 0} total={obj.retro_streak_target || 3} testId="career-retro-streak" />
                  {t('career.streakOf', '{{n}} de {{total}} meses', { n: data.retro_streak || 0, total: obj.retro_streak_target || 3 })}
                </span>
              </div>
              {(data.team?.juniors || []).length > 0 && (
                <div className="rounded-lg border">
                  <Table data-testid="career-team-juniors-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">{t('career.agent', 'Agente')}</TableHead>
                        <TableHead className="text-xs text-right">{t('career.sold', 'Vendido')}</TableHead>
                        <TableHead className="text-xs text-right">{t('career.collected', 'Cobrado')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.team.juniors.map((j) => (
                        <TableRow key={j.agent_id}>
                          <TableCell className="text-sm py-2">{j.name}</TableCell>
                          <TableCell className="text-sm py-2 text-right tabular-nums">{fmtUSD(j.sold)}</TableCell>
                          <TableCell className="text-sm py-2 text-right tabular-nums">{fmtUSD(j.collected)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Storico ultimi mesi */}
        {(data.history || []).length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-2">
              {t('career.history', 'Últimos meses')}
            </div>
            <div className="flex gap-2 flex-wrap" data-testid="career-history">
              {data.history.map((h) => (
                <div key={`${h.year}-${h.month}`} className="rounded-md border px-2.5 py-1.5 text-xs bg-card">
                  <div className="text-muted-foreground">{monthLabel(i18n.language, h.year, h.month).split(' ')[0]}</div>
                  <div className="font-medium tabular-nums">
                    {level === 'setter' && `${h.show_pct ?? 0}%`}
                    {level === 'junior' && fmtUSD(h.sold)}
                    {level === 'senior' && fmtUSD(h.team_sold)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA collegate al resto del CRM */}
        <div className="flex gap-2 flex-wrap pt-1 border-t">
          <Link to="/app/leads">
            <Button variant="outline" size="sm" data-testid="career-link-leads">
              <Users className="w-3.5 h-3.5 mr-1.5" />{t('career.connectLeads', 'Mis Leads')}
              <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
          <Link to="/app/calendar">
            <Button variant="outline" size="sm" data-testid="career-link-calendar">
              <CalendarDays className="w-3.5 h-3.5 mr-1.5" />{t('career.connectCalendar', 'Calendario')}
              <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
          <Link to="/app/pipeline">
            <Button variant="outline" size="sm" data-testid="career-link-pipeline">
              <KanbanSquare className="w-3.5 h-3.5 mr-1.5" />{t('career.connectPipeline', 'Pipeline')}
              <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      </CardContent>

      {/* Dialog tabella bonus Setter */}
      <Dialog open={tableOpen} onOpenChange={setTableOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="career-bonus-table-dialog">
          <DialogHeader>
            <DialogTitle>{t('career.bonusTable', 'Tabla de bonos mensual — Setter')}</DialogTitle>
            <DialogDescription>
              {t('career.bonusTableDesc', 'Bono según el % de shows del mes (shows / leads asignados).')}
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">{t('career.pctShows', '% Shows')}</TableHead>
                <TableHead className="text-xs text-right">{t('career.bonusUsd', 'Bono (USD)')}</TableHead>
                <TableHead className="text-xs text-right">{t('career.totalWithFixed', 'Total con fijo')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(plan?.setter?.bonus_steps || []).map((s) => {
                const fixed = data.career_milestone ? plan.setter.fixed_milestone : plan.setter.fixed_base;
                const active = data.show_pct >= s.pct &&
                  (plan.setter.bonus_steps.find((x) => x.pct > s.pct)?.pct || 101) > data.show_pct;
                return (
                  <TableRow key={s.pct} className={active ? 'bg-emerald-50' : ''}>
                    <TableCell className="py-1.5 text-sm">{s.pct}%</TableCell>
                    <TableCell className="py-1.5 text-sm text-right tabular-nums">{fmtUSD(s.bonus)}</TableCell>
                    <TableCell className="py-1.5 text-sm text-right tabular-nums">{fmtUSD(fixed + s.bonus)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
